import { scopedApi, scopedChannelApi } from '@/session/scopedClients';
import type { RequestScope } from '@/session/scopedTransport';
import type { EndpointResponse } from '@/types/endpoints';
import { utils } from '@/utils';
import type { ReadRequestOptions } from '../requestOptions';

type DynamicListParams = Record<string, unknown>;
type CountResult = { count: number };

const safePathSegment = (segment: string): string => {
  if (!/^[A-Za-z0-9_.:%,-]+$/.test(segment) || segment.includes('..')) throw new Error('Invalid dynamic list path');
  return segment;
};

const safePath = (path: string): string => {
  const segments = path.split('/');
  const firstSegment = path.startsWith('/') ? 1 : 0;
  if (segments.length === firstSegment || segments.slice(firstSegment).some((segment) => !segment)) throw new Error('Invalid dynamic list path');
  segments.slice(firstSegment).forEach(safePathSegment);
  return path;
};

const apiGet = async <T>(
  scope: RequestScope,
  path: string,
  params: DynamicListParams | undefined,
  options: ReadRequestOptions
): Promise<EndpointResponse<T>> => {
  try {
    const response = await scopedApi(scope).get<EndpointResponse<T>>(safePath(path), { ...options, params });
    return response.data;
  } catch (error) {
    return utils.error.handleAxiosError(error);
  }
};

// Row endpoints take the caller's row type the way getRecord<T> does: an unconstrained
// <T> over the whole result is the only shape BoundEndpoint keeps generic.
export const getDynamicSearchRows = <T>(
  scope: RequestScope,
  params: { modelName: string; query: DynamicListParams },
  options: ReadRequestOptions
): Promise<EndpointResponse<T>> => apiGet<T>(scope, `/search/${safePathSegment(params.modelName)}`, params.query, options);

export const getDynamicSearchCount = (
  scope: RequestScope,
  params: { modelName: string; query: DynamicListParams },
  options: ReadRequestOptions
): Promise<EndpointResponse<CountResult>> => apiGet(scope, `/search/${safePathSegment(params.modelName)}/count`, params.query, options);

export const getDynamicRows = <T>(
  scope: RequestScope,
  params: { modelName: string; query: DynamicListParams },
  options: ReadRequestOptions
): Promise<EndpointResponse<T>> => apiGet<T>(scope, `/${safePathSegment(params.modelName)}`, params.query, options);

export const getDynamicCount = (
  scope: RequestScope,
  params: { modelName: string; query: DynamicListParams },
  options: ReadRequestOptions
): Promise<EndpointResponse<CountResult>> => apiGet(scope, `/${safePathSegment(params.modelName)}/count`, params.query, options);

// The channel API answers with a bare `{ data, total }` payload rather than the platform
// envelope, so it is returned as-is for the caller to normalize.
export const getDynamicChannelRows = async <T>(
  scope: RequestScope,
  params: { modelName: string; subscriptionId?: string | number; limit: number; page: number; sort?: string },
  options: ReadRequestOptions
): Promise<EndpointResponse<T>> => {
  try {
    const response = await scopedChannelApi(scope).get<T>(`/astur/integrations/${safePathSegment(params.modelName)}`, {
      ...options,
      params: { limit: params.limit, page: params.page, sort: params.sort },
      headers: params.subscriptionId === undefined ? undefined : { 'X-Astur-Subscription-Id': params.subscriptionId },
    });
    const payload: unknown = response.data;
    const rows = typeof payload === 'object' && payload !== null && 'data' in payload && Array.isArray(payload.data) ? payload.data : [];
    return { result: response.data, status: response.status, execution_time: 0, num_results: rows.length };
  } catch (error) {
    return utils.error.handleAxiosError(error);
  }
};
